
import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { 
  Users, Package, FileText, DollarSign, 
  Settings, LogOut, X
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { UserIdentity } from "@/components/UserIdentity";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showCustomerTable, setShowCustomerTable] = useState(false);
  
  const menuItems = [
    { icon: DollarSign, label: "Fluxo de Caixa", href: "/adm/dashboard" },
    { icon: Users, label: "Clientes", href: "#customers" },
    { icon: Package, label: "Estoque", href: "/adm/stock" },
    { icon: FileText, label: "Relatórios", href: "/adm/reports" },
    { icon: FileText, label: "Orçamentos", href: "/adm/quotations" },
    { icon: Settings, label: "Configurações", href: "/adm/settings" },
  ];

  useEffect(() => {
    if (!menuRef.current || !itemsRef.current) return;
    
    const tl = gsap.timeline({ paused: true });
    
    // Menu animation
    tl.fromTo(
      menuRef.current,
      { x: "-100%" },
      { x: "0%", duration: 0.5, ease: "power3.out" }
    );
    
    // Items animation - staggered entrance
    tl.fromTo(
      itemsRef.current.children,
      { x: -30, opacity: 0 },
      { x: 0, opacity: 1, stagger: 0.05, duration: 0.4, ease: "back.out" },
      "-=0.2"
    );
    
    if (isOpen) {
      tl.play();
      document.body.style.overflow = "hidden"; // Prevent scrolling when menu is open
    } else {
      tl.reverse();
      document.body.style.overflow = ""; // Allow scrolling when menu is closed
    }
    
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>, href: string) => {
    e.preventDefault();
    
    if (href === "#") return;
    
    if (href === "#customers") {
      // Adiciona o componente CustomerTable à rota atual
      const currentLocation = window.location.pathname;
      onClose();
      navigate(currentLocation, { state: { showCustomerTable: true } });
      
      toast({
        title: "Clientes",
        description: "Abrindo gerenciamento de clientes",
      });
    } else {
      onClose();
      navigate(href);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      onClose();
      navigate("/auth");
      
      toast({
        title: "Logout realizado com sucesso",
        description: "Você foi desconectado do sistema",
      });
    } catch (error) {
      toast({
        title: "Erro ao realizar logout",
        description: "Tente novamente mais tarde",
        variant: "destructive",
      });
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-50 ${isOpen ? "pointer-events-auto" : "pointer-events-none"}`}
    >
      {/* Overlay background with blur */}
      <div 
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      
      {/* Menu panel */}
      <div 
        ref={menuRef}
        className="absolute inset-y-0 left-0 w-[85%] max-w-xs bg-gradient-to-br from-[#0073B7] to-[#004080] shadow-xl flex flex-col"
      >
        {/* Logo & Header */}
        <div className="flex flex-col items-center p-4 space-y-2">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white rounded-full p-1 hover:bg-white/10"
          >
            <X className="h-6 w-6" />
          </button>
          
          <img 
            src="/lovable-uploads/df36f051-6586-421c-8e3f-5e4e41162b5f.png" 
            alt="Solucionar Soldas Logo" 
            className="w-20 h-20 object-contain mt-6 drop-shadow-lg"
          />
          <h1 className="text-xl font-bold text-white font-['Russo_One'] drop-shadow-md">
            Solucionar Soldas
          </h1>
        </div>

        <UserIdentity variant="menu" />
        
        
        {/* Menu Items */}
        <div className="overflow-y-auto flex-1 py-4">
          <div ref={itemsRef} className="space-y-1 px-2">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={(e) => handleMenuClick(e, item.href)}
                className="flex items-center w-full gap-3 px-4 py-3 rounded-lg transition-colors text-white hover:bg-white/10 active:bg-white/20"
              >
                <item.icon className="h-5 w-5" />
                <span className="font-['Roboto'] font-medium">{item.label}</span>
              </button>
            ))}
            
            <div className="my-4 border-t border-[#ffffff33] mx-2"></div>
            
            <button
              onClick={handleLogout}
              className="flex items-center w-full gap-3 px-4 py-3 rounded-lg transition-colors text-white hover:bg-[#E60026]/20 active:bg-[#E60026]/30"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-['Roboto'] font-medium">Sair</span>
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 text-center text-white/70 text-sm">
          <p>© 2024 Solucionar Soldas</p>
        </div>
      </div>
    </div>
  );
}
